/**
 * Sentry observability bootstrap.
 *
 * Fully optional. When VITE_SENTRY_DSN is not defined the module is a no-op,
 * so the application ships and runs normally without any Sentry credentials.
 * Enable in production by populating the environment variables documented in
 * docs/SENTRY_SETUP.md.
 */
import * as Sentry from "@sentry/react";

let initialized = false;

export function initObservability() {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return; // Disabled until credentials are provided.

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.MODE === "production" ? "production" : "development");
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE ?? 0),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE ?? 1),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Strip common PII before it leaves the browser.
    beforeSend(event) {
      if (event.request?.cookies) delete event.request.cookies;
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
      }
      return event;
    },
  });

  initialized = true;
}

export function captureException(err: unknown, context?: Record<string, unknown>) {
  if (!initialized) {
    // Local visibility when Sentry is not configured yet.
    // eslint-disable-next-line no-console
    console.error("[observability]", err, context);
    return;
  }
  Sentry.captureException(err, { extra: context });
}

export { Sentry };